class CartDrawer extends SideDrawer {
  constructor() {
    super();
  }

  open(triggeredBy) {
    super.open(triggeredBy);
    const cartDrawerDetails = this.querySelectorAll('[id^="Details-"] summary');
    [...cartDrawerDetails].forEach((summary) => this.setSummaryAccessibility(summary));
  }

  close() {
    super.close();
    const cartDrawerDetails = this.querySelectorAll('[id^="Details-"] summary');
    [...cartDrawerDetails].forEach((summary) => {
      summary.parentElement.removeAttribute('open');
      summary.setAttribute('aria-expanded', false);
    });
  }

  setSummaryAccessibility(summary) {
    if (summary.hasAttribute('role')) return;
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', 'false');
    if (summary.nextElementSibling.getAttribute('id')) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', (event) => event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open')));
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.cart = this.closest('.cart-wrapper');
    const debouncedOnChange = debounce((event) => this.onChange(event), ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
    this.validateBeforeCheckout();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  validateBeforeCheckout() {
    const form = document.getElementById('CartForm');
    const closeIcon = document.querySelectorAll('.custom-form__message-box .custom-close-icon');
    const error = document.getElementById('CartQuantityError');

    if (closeIcon) {
      closeIcon.forEach((icon) => {
        icon.addEventListener('click', () => {
          if (error && error.classList.contains('show')) {
            error.classList.remove('show');
          }
        })
      })
    }

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      fetch('/cart/update.js')
      .then(response => response.json())
      .then(cart => {
        const itemCount = cart.item_count;
        if (itemCount > 10) {
          if (error) {
            error.classList.add('show');
          }
        } else {
          if (error && error.classList.contains('show')) {
            error.classList.remove('show');
          }
          form.submit();
          window.location.href = '/checkout';
        }
      })
      .catch(error => {
        console.error('Error fetching the cart:', error);
      });
    })
  }

  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target.dataset.quantityVariantId);
  }

  onCartUpdate() {
    let section_id = this.tagName === 'CART-DRAWER-ITEMS' ? 'cart-drawer' : 'main-cart-items';
    let selectors = this.tagName === 'CART-DRAWER-ITEMS' ? ['cart-drawer-items'] : ['cart-items'];

    fetch(`${routes.cart_url}?section_id=${section_id}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  renderContents(parsedState, count) {
    this.getSectionsToRender().forEach((section) => {
      const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
      for (let selector of this.getSelectorsToRender(section.section)) {
        const destination = document.getElementById(selector);
        const source = html.getElementById(selector);
        if (!destination || !source) continue;
        destination.innerHTML = source.innerHTML;
      }
    });
    this.cart.classList.toggle('is-empty', count === 0);
    this.updateCount(count);
  }

  getSectionsToRender() {
    let sections = [{
      id: 'main-cart-items',
      section: document.getElementById('main-cart-items').dataset.id
    }];
    if (window.cartConfigs.drawer) {
      sections = [
        ...sections,
        {
          id: 'cart-drawer-items',
          section: document.getElementById('cart-drawer-items').dataset.id
        }
      ]
    }

    return sections
  }

  getSelectorsToRender(sectionId) {
    let selectors = [
      `CartItems-${sectionId}`,
      `CartDrawerItems-${sectionId}`,
      `CartTotals-${sectionId}`,
      `CartDrawerTotals-${sectionId}`,
      `CartMessage-${sectionId}`,
      `CartDrawerMessage-${sectionId}`,
      `CartGiftWrapping-${sectionId}`,
      `CartDrawerGiftWrapping-${sectionId}`,
      `CartLineItemStatus-${sectionId}`,
      `CartDrawerLineItemStatus-${sectionId}`,
      `CartLiveRegionText-${sectionId}`,
      `CartDrawerLiveRegionText-${sectionId}`
    ]

    return selectors
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading();
    const quantityElement = this.querySelector(`input[name="updates[]"][data-index="${line}"`);
    const items = this.querySelectorAll('.cart-item');
    const drawer = this.closest('cart-drawer');
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.renderContents(parsedState, parsedState.item_count);
        let message = '';
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          let message = typeof updatedValue === 'undefined' ? window.cartStrings.error : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);
        this.updateLineItems(line, name, parsedState.item_count, drawer);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((e) => {
        this.updateErrors();
        console.error(e);
      })
      .finally(() => {
        this.disableLoading();
      });
  }

  updateLiveRegions(line, message = '') {
    const selectors = [
      `CartErrorLine-${line}`,
      `CartDrawerErrorLine-${line}`
    ];
    for (let selector of selectors) {
      const lineItemError = document.getElementById(selectors);
      if (lineItemError) {
        lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
        lineItemError.classList.toggle('hidden', message === '');
      }
    }
  }

  updateLineItems(line, name, count, drawer = undefined) {
    const selectors = [
      `CartItem-${line}`,
      `CartDrawerItem-${line}`
    ];
    for (let selector of selectors) {
      const lineItem = document.getElementById(selectors);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        drawer ? trapFocus(drawer, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
      } else if (count === 0 && drawer) {
        trapFocus(drawer.querySelector('.drawer__inner'), drawer.querySelector('a'));
      } else if (document.querySelector('.cart-item') && drawer) {
        trapFocus(drawer, document.querySelector('.cart-item__name'));
      }
    }
  }

  updateErrors() {
    const selectors = [
      `CartErrors`,
      `CartDrawerErrors`
    ];
    for (let selector of selectors) {
      const errorElement = document.getElementById(selectors);
      if (!errorElement) continue;
      errorElement.querySelector('.form__message-text').textContent = window.cartStrings.error;
      errorElement.removeAttribute('hidden');
    }
    const loadingOverlays = document.querySelectorAll(this.dataset.loadingOverlay);
    [...loadingOverlays].forEach((overlay) => overlay.disableLoading());
  }

  updateCount(count){
    const countElements = document.querySelectorAll('[data-cart-count]');
    [...countElements].forEach((countElement) => {
      let cartCountFormat = countElement.dataset.cartCountFormat;
      countElement.textContent = cartCountFormat ? cartCountFormat.replace('[count]', count) : count;
    });
  }

  enableLoading() {
    const selectors = [
      `main-cart-items`,
      `cart-drawer-items`
    ];
    for (let selector of selectors) {
      const element = document.getElementById(selectors);
      if (!element) continue;
      element.classList.add('pe-none');
    }
    const loadingOverlays = document.querySelectorAll(this.dataset.loadingOverlay);
    [...loadingOverlays].forEach((overlay) => overlay.enableLoading());
    document.activeElement.blur();
  }

  disableLoading() {
    const selectors = [
      `main-cart-items`,
      `cart-drawer-items`
    ];
    for (let selector of selectors) {
      const element = document.getElementById(selectors);
      if (!element) continue;
      element.classList.remove('pe-none');
    }
    const loadingOverlays = document.querySelectorAll(this.dataset.loadingOverlay);
    [...loadingOverlays].forEach((overlay) => overlay.disableLoading());
  }
}

customElements.define('cart-items', CartItems);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cart = this.closest('.cart-drawer');
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateBeforeCheckout();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  validateBeforeCheckout() {
    const form = document.getElementById('CartDrawerForm');
    const closeIcon = document.querySelectorAll('.custom-form__message-box .custom-close-icon');
    const error = document.getElementById('CartQuantityErrorDrawer');

    if (closeIcon) {
      closeIcon.forEach((icon) => {
        icon.addEventListener('click', () => {
          if (error && error.classList.contains('show')) {
            error.classList.remove('show');
          }
        })
      })
    }

    form.addEventListener('submit', (e) => {
      console.log("Submiited")
      e.preventDefault();
      fetch('/cart/update.js')
      .then(response => response.json())
      .then(cart => {
        const itemCount = cart.item_count;
        if (itemCount > 10) {
          if (error) {
            error.classList.add('show');
          }
        } else {
          if (error && error.classList.contains('show')) {
            error.classList.remove('show');
          }
          form.submit();
          window.location.href = '/checkout';
        }
      })
      .catch(error => {
        console.error('Error fetching the cart:', error);
      });
    })
  }

  renderContents(parsedState, count) {
    super.renderContents(parsedState, count);
    document.body.dispatchEvent(new CustomEvent('cartDrawerRendered'));
  }

  getSectionsToRender() {
    let sections = [{
      id: 'cart-drawer-items',
      section: document.getElementById('cart-drawer-items').dataset.id
    }];
    if (window.cartConfigs.page) {
      sections = [
        ...sections,
        {
          id: 'main-cart-items',
          section: document.getElementById('main-cart-items').dataset.id
        }
      ]
    }
    return sections
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener('change', debounce((event) => {
          const body = JSON.stringify({ note: event.target.value });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('cart-message')) {
  customElements.define(
    'cart-message',
    class CartMessage extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.calculator();
      }

      calculator() {
        let rate = Shopify.currency.rate || 1;
        this.price = parseInt(this.dataset.price) * rate;
        this.total = parseInt(this.dataset.total);
        this.symbol = this.dataset.symbol;
        this.firstText = this.dataset.firstText;
        this.secondText = this.dataset.secondText;
        this.thirdText = this.dataset.thirdText;
        let freeshipText, freeshipTextHTML, percent, freeshipClass = 'none';
        percent = ((this.total/this.price) * 100).toFixed(2);

        if(this.total == 0) {
          this.price = Shopify.formatMoney(this.price, window.shopMoney.money_format);
          freeshipText = this.firstText.replace('{{ price }}', this.price);
          freeshipTextHTML = this.firstText.replace('{{ price }}', `<strong>${this.price}</strong>`);
        } else if (this.total > this.price) {
          freeshipText = this.secondText;
          freeshipTextHTML = this.secondText;
          freeshipClass = 'progress-free';
          percent = 100;
        } else {
          let extraPrice = Math.abs(this.total - this.price);
          extraPrice = Shopify.formatMoney(extraPrice, window.shopMoney.money_format);
          freeshipText = this.thirdText.replace('{{ price }}', extraPrice);
          freeshipTextHTML = this.thirdText.replace('{{ price }}', `<strong>${extraPrice}</strong>`);

          if(percent <= 30) {
            freeshipClass = 'progress-30';
          } else if(percent <= 60) {
            freeshipClass = 'progress-60';
          } else if(percent <= 100) {
            freeshipClass = 'progress-99';
          }
        }

        this.querySelector('.progress')?.classList.add(freeshipClass);
        this.querySelector('.message').innerHTML = freeshipTextHTML;
        this.querySelector('.progress__shipping')?.setAttribute('aria-label', freeshipText);
        this.querySelector('.progress__meter')?.style.setProperty('--percent', `${percent}%`);
      }
    }
  );
}

if (!customElements.get('cart-shipping-calculator')) {
  customElements.define(
    'cart-shipping-calculator',
    class CartShippingCalculator extends HTMLElement {
      constructor() {
        super();

        this.zip = this.querySelector('[id^="ShippingCalculatorZip-"]');
        this.country = this.querySelector('[id^="ShippingCalculatorCountry-"]');
        this.province = this.querySelector('[id^="ShippingCalculatorProvince-"]');
        this.submitButton = this.querySelector('button');
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
        this.submitButtonText = this.submitButton.querySelector('span');
        this.orginalSubmitButtonText = this.submitButtonText.textContent;
      }

      connectedCallback() {
        this.setupCountries(this.dataset.id);
      }

      setupCountries(sectionId) {
        if (Shopify && Shopify.CountryProvinceSelector) {
          // eslint-disable-next-line no-new
          new Shopify.CountryProvinceSelector(`ShippingCalculatorCountry-${sectionId}`, `ShippingCalculatorProvince-${sectionId}`, {
            hideElement: `ShippingCalculatorProvinceContainer-${sectionId}`
          });
        }
      }

      onSubmitHandler(event) {
        event.preventDefault();

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        this.handleMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButtonText.textContent = window.form.loading;

        const body = JSON.stringify({
          shipping_address: {
            zip: this.zip.value,
            country: this.country.value,
            province: this.province.value
          }
        });
        let sectionUrl = `${routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...fetchConfig('javascript'), body })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              if(parsedState.shipping_rates.length) {
                parsedState.shipping_rates.forEach((rate) => {
                  const message = `${rate.name}: ${rate.price} ${Shopify.currency.active}`;
                  this.handleMessage(message, 'success');
                });
              } else {
                this.handleMessage(window.cartStrings.shippingInfo, 'info');
              }
            } else {
              let errors = [];
              Object.entries(parsedState).forEach(([attribute, messages]) => {
                errors.push(`${attribute.charAt(0).toUpperCase() + attribute.slice(1)} ${messages[0]}`);
              });
              this.handleMessage(errors.join('; '), 'error');
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButtonText.textContent = this.orginalSubmitButtonText;
            this.submitButton.removeAttribute('aria-disabled');
          });
      }

      handleMessage(message = false, type = 'error') {
        this.messageWrapper = this.messageWrapper || this.querySelector('.shipping-calculator__message-wrapper');
        if (!this.messageWrapper) return;
        this.messageBox = this.messageBox || this.messageWrapper.querySelector('.form__message');
        this.messageSpan = this.messageSpan || this.messageWrapper.querySelector('.form__message-span');
        this.messageText = this.messageText || this.messageWrapper.querySelector('.form__message-text');
        if (message) {
          this.messageSpan.textContent = (type === 'error' ?  window.form.error : window.form.info);
          this.messageText.textContent = message;
          this.messageBox.classList.toggle('form__message--error', (type === 'error'));
        }
        this.messageWrapper.toggleAttribute('hidden', !message);
      }
    }
  );
}

if (!customElements.get('gift-wrapping')) {
  customElements.define(
    'gift-wrapping',
    class GiftWrapping extends HTMLElement {
      constructor() {
        super();

        this.cartItemsDrawer = document.querySelector('cart-drawer-items');
        this.giftWrapping = this.dataset.giftWrapping;
        this.giftWrappingId = this.dataset.giftWrappingId;
        this.cartItemsSize = parseInt(this.getAttribute('cart-items-size'));
        this.giftWrappingInCart = parseInt(this.getAttribute('gift-wrapping-in-cart'));
        this.itemsInCart = parseInt(this.getAttribute('items-in-cart'));
        this.checkboxInput = this.querySelector(`#Gift-wrapping-checkbox-${this.dataset.sectionId}`);
        this.buttons = this.querySelectorAll(`[name="add"], [name="remove"]`);

        if(this.checkboxInput) this.checkboxInput.addEventListener('change', this.onChange.bind(this));
        [...this.buttons].forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));

        if(this.giftWrappingInCart > 0) {
          // If we have nothing but gift-wrap items in the cart.
          if (this.cartItemsSize == 1) this.removeGiftWrapping();
          // If we don't have the right amount of gift-wrap items in the cart.
          if(this.giftWrappingInCart != this.itemsInCart) this.addGiftWrapping();
          // If we have a gift-wrap item in the cart but our gift-wrapping cart attribute has not been set.
          if (this.giftWrapping.length == 0) this.addGiftWrapping();
        } else {
          // If we have no gift-wrap item in the cart but our gift-wrapping cart attribute has been set.
          if(this.giftWrapping > 0) this.addGiftWrapping();
        }
      }

      onChange() {
        this.checkboxInput.checked ? this.addGiftWrapping() : this.removeGiftWrapping();
      }

      onButtonClick(event) {
        event.preventDefault();
        event.currentTarget.getAttribute('name') === 'add' ? this.addGiftWrapping() : this.removeGiftWrapping();
      }

      addGiftWrapping() {
        let attributes = {
          'gift-wrapping': true
        }

        this.getContentFormFetch(this.itemsInCart, attributes);
      }

      removeGiftWrapping() {
        let attributes = {
          'gift-wrapping': '',
          'gift-wrapping-note': ''
        }
        
        this.getContentFormFetch(0, attributes);
      }

      getContentFormFetch(quantity, attributes) {
        this.enableLoading();
        const body = JSON.stringify({
          updates: {
            [this.giftWrappingId]: quantity
          },
          attributes: attributes,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        fetch(`${routes.cart_update_url}`, {...fetchConfig(), ...{ body }})
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.renderContents(parsedState, parsedState.item_count);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.disableLoading();
          });
      }

      renderContents(parsedState, count) {
        this.getSectionsToRender().forEach((section) => {
          const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
          for (let selector of this.getSelectorsToRender(section.section)) {
            const destination = document.getElementById(selector);
            const source = html.getElementById(selector);
            if (!destination || !source) continue;
            destination.innerHTML = source.innerHTML;
          }
        });
        this.updateCount(count);
      }

      getSectionsToRender() {
        let sections = [];
        if (window.cartConfigs.drawer) {
          sections = [
            ...sections,
            {
              id: 'cart-drawer-items',
              section: document.getElementById('cart-drawer-items').dataset.id
            }
          ]
        }
        if (window.cartConfigs.page) {
          sections = [
            ...sections,
            {
              id: 'main-cart-items',
              section: document.getElementById('main-cart-items').dataset.id
            }
          ]
        }
        return sections;
      }

      getSelectorsToRender(sectionId) {
        let selectors = [
          `CartItems-${sectionId}`,
          `CartDrawerItems-${sectionId}`,
          `CartTotals-${sectionId}`,
          `CartDrawerTotals-${sectionId}`,
          `CartMessage-${sectionId}`,
          `CartDrawerMessage-${sectionId}`,
          `CartGiftWrapping-${sectionId}`,
          `CartDrawerGiftWrapping-${sectionId}`,
          `CartLineItemStatus-${sectionId}`,
          `CartDrawerLineItemStatus-${sectionId}`,
          `CartLiveRegionText-${sectionId}`,
          `CartDrawerLiveRegionText-${sectionId}`
        ]

        return selectors
      }

      updateCount(count){
        const countElements = document.querySelectorAll('[data-cart-count]');
        [...countElements].forEach((countElement) => {
          let cartCountFormat = countElement.dataset.cartCountFormat;
          countElement.textContent = cartCountFormat ? cartCountFormat.replace('[count]', count) : count;
        });
      }

      enableLoading() {
        const selectors = [
          `main-cart-items`,
          `cart-drawer-items`
        ];
        for (let selector of selectors) {
          const element = document.getElementById(selectors);
          if (!element) continue;
          element.classList.add('pe-none');
        }
        const loadingOverlays = document.querySelectorAll(this.dataset.loadingOverlay);
        [...loadingOverlays].forEach((overlay) => overlay.enableLoading());
        document.activeElement.blur();
      }

      disableLoading() {
        const selectors = [
          `main-cart-items`,
          `cart-drawer-items`
        ];
        for (let selector of selectors) {
          const element = document.getElementById(selectors);
          if (!element) continue;
          element.classList.remove('pe-none');
        }
        const loadingOverlays = document.querySelectorAll(this.dataset.loadingOverlay);
        [...loadingOverlays].forEach((overlay) => overlay.disableLoading());
      }
    }
  );
}

if (!customElements.get('gift-wrapping-note')) {
  customElements.define(
    'gift-wrapping-note',
    class GiftWrappingNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', debounce((event) => {
          const body = JSON.stringify({ attributes: { 'gift-wrapping-note': event.target.value } });
          fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!customElements.get('gift-quantity-input')) {
  customElements.define(
    'gift-quantity-input',
    class GiftQuantityInput extends QuantityInput {
      constructor() {
        super();

        const giftWrappings = document.querySelectorAll('gift-wrapping');
        this.cartItemsSize = parseInt(this.getAttribute('cart-items-size'));
        this.giftWrappingInCart = parseInt(this.getAttribute('gift-wrapping-in-cart'));
        this.itemsInCart = parseInt(this.getAttribute('items-in-cart'));
        if(this.giftWrappingInCart > 0) {
          // If we have nothing but gift-wrap items in the cart.
          if (this.cartItemsSize == 1) [...giftWrappings].forEach((giftWrapping) => giftWrapping.removeGiftWrapping());
          // If we don't have the right amount of gift-wrap items in the cart.
          if (this.giftWrappingInCart != this.itemsInCart) this.update(this.itemsInCart);
          // If we have a gift-wrap item in the cart but our gift-wrapping cart attribute has not been set.
          if (giftWrappings.length == 0) this.update();
        } else {
          // If we have no gift-wrap item in the cart but our gift-wrapping cart attribute has been set.
          if (giftWrappings.length > 0) this.update(this.itemsInCart);
        }
      }

      update(quantity = 0) {
        this.input.value = quantity;
        this.input.dispatchEvent(this.changeEvent);
      }
    }
  );
}